import { DataRefreshEvent } from "@fp/admin-api-client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { useGetSettings } from "@fp/admin-api-client/settings/settings.client";
import {
  getGetPendingTalentReferralsQueryKey,
  useGetPendingTalentReferrals,
} from "@fp/admin-api-client/talent-referrals/talent-referrals.client";
import { OrganizationAvatar } from "@fp/ui/components/base/avatar/organization-avatar";
import { Dropdown } from "@fp/ui/components/base/dropdown/dropdown";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Edit05,
  Flag01,
  LayersThree01,
  LogOut01,
  Mail01,
  PuzzlePiece02,
  Settings01,
  SwitchHorizontal01,
  Tool02,
  User01,
  UserPlus01,
  Users01,
  Wallet02,
} from "@untitledui/icons";
import { useCallback, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { useDataRefresh } from "@/hooks/useSignalR";
import axios from "@/lib/axios";
import { isAuthorizedForDevTools } from "@/lib/dev-tools-access";
import { useBatchProcesses } from "./batch-process/batch-process-context";
import { OrgSwitcherModal } from "./org-switcher-modal";
import type { PageTarget } from "./page-back-button";
import { useUserContext } from "./user-context";

interface ReceivedInvitationsResponse {
  count: number;
}

interface PendingJoinRequestDto {
  id: string;
}

export function AccountMenu({ from }: { from: PageTarget }) {
  const { claims, organizations, orgId, logout } = useUserContext();
  const navigate = useNavigate();
  const returnState = { returnTo: from.url, returnLabel: from.label };
  const queryClient = useQueryClient();
  const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data: settingsResponse } = useGetSettings();
  const showDevToolsLink = isAuthorizedForDevTools(
    claims?.email,
    claims?.emailVerified ?? false,
    settingsResponse?.data?.showDevTools,
  );

  const { canView: canViewBatchProcesses } = useBatchProcesses();

  const { data: capabilitiesResponse } = useGetOrganizationContextCapabilities();
  const canViewFunding = capabilitiesResponse?.data?.canViewFunding ?? false;
  const canUseSensitiveDevTools = capabilitiesResponse?.data?.canUseSensitiveDevTools ?? false;
  const canListTalentReferrals = capabilitiesResponse?.data?.canListTalentReferrals ?? false;

  const { data: invitationsResponse } = useQuery({
    queryKey: ["received-invitations"],
    queryFn: async () => {
      const response = await axios.get<ReceivedInvitationsResponse>(
        "/api/received-invitations",
      );
      return response.data;
    },
  });

  const { data: pendingRequests } = useQuery({
    queryKey: ["pending-join-requests", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const response = await axios.get<PendingJoinRequestDto[]>(
        `/api/join-requests/organization/${orgId}/pending`,
      );
      return response.data;
    },
    enabled: !!orgId,
  });

  const { data: pendingReferralsResponse } = useGetPendingTalentReferrals({
    query: { queryKey: [...getGetPendingTalentReferralsQueryKey(), { orgId }], enabled: !!orgId && canListTalentReferrals },
  });

  const invalidateBadgeCounts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["received-invitations"] });
    queryClient.invalidateQueries({
      queryKey: ["pending-join-requests", orgId],
    });
    queryClient.invalidateQueries({ queryKey: getGetPendingTalentReferralsQueryKey() });
  }, [queryClient, orgId]);

  useDataRefresh(DataRefreshEvent.InvitationCreated, invalidateBadgeCounts);
  useDataRefresh(DataRefreshEvent.JoinRequestCreated, invalidateBadgeCounts);
  useDataRefresh(DataRefreshEvent.JoinRequestApproved, invalidateBadgeCounts);

  const invitationCount = invitationsResponse?.count ?? 0;
  const joinRequestCount = pendingRequests?.length ?? 0;
  const referralCount = pendingReferralsResponse?.data.length ?? 0;

  const currentOrg = organizations.find((org) => org.organizationId === orgId);

  if (!currentOrg || organizations.length === 0) {
    return null;
  }

  return (
    <>
      <Dropdown.Root isOpen={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <AriaButton
          className="rounded-lg p-1 hover:bg-gray-100 cursor-pointer outline-none"
          aria-label="Account menu"
        >
          <OrganizationAvatar
            name={currentOrg.organizationName}
            logoUrl={currentOrg.logoUrl}
            size="sm"
          />
        </AriaButton>
        <Dropdown.Popover placement="bottom end" className="w-64 -mr-2">
          <div className="py-1">
            <div className="flex items-center gap-3 px-3 py-2">
              <OrganizationAvatar
                name={currentOrg.organizationName}
                logoUrl={currentOrg.logoUrl}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {currentOrg.organizationName}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {claims?.email}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                  if (organizations.length > 1) {
                    setIsOrgSwitcherOpen(true);
                  } else {
                    navigate({ to: "/organizations", state: returnState });
                  }
                }}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 cursor-pointer"
                aria-label={
                  organizations.length > 1
                    ? "Switch organization"
                    : "Edit organization"
                }
              >
                {organizations.length > 1 ? (
                  <SwitchHorizontal01 className="size-5" />
                ) : (
                  <Edit05 className="size-5" />
                )}
              </button>
            </div>
          </div>
          <Dropdown.Separator />
          <Dropdown.Menu aria-label="Account actions">
            <Dropdown.Section>
              <Dropdown.SectionHeader className="px-4 pt-1 pb-1 text-xs font-semibold text-brand-600 uppercase tracking-wider">
                Account
              </Dropdown.SectionHeader>
              <Dropdown.Item
                id="profile"
                icon={User01}
                label="Profile"
                onAction={() => navigate({ to: "/profile", state: returnState })}
              />
              {canViewFunding && (
                <Dropdown.Item
                  id="funding"
                  icon={Wallet02}
                  label="Funding"
                  onAction={() => navigate({ to: "/funding", state: returnState })}
                />
              )}
              <Dropdown.Item
                id="invitations"
                icon={Mail01}
                label="Invitations"
                badge={invitationCount}
                onAction={() => navigate({ to: "/invitations", state: returnState })}
              />
              <Dropdown.Item
                id="join-requests"
                icon={Users01}
                label="Join Requests"
                badge={joinRequestCount}
                onAction={() => navigate({ to: "/join-requests", state: returnState })}
              />
              {canListTalentReferrals && (
                <Dropdown.Item
                  id="referrals"
                  icon={UserPlus01}
                  label="Referrals"
                  badge={referralCount}
                  onAction={() => navigate({ to: "/referrals", state: returnState })}
                />
              )}
              <Dropdown.Item
                id="integrations"
                icon={PuzzlePiece02}
                label="Integrations"
                onAction={() => navigate({ to: "/integrations", state: returnState })}
              />
              {canViewBatchProcesses && (
                <Dropdown.Item
                  id="batch-processes"
                  icon={LayersThree01}
                  label="Batch Processes"
                  onAction={() => navigate({ to: "/batch-processes", state: returnState })}
                />
              )}
              <Dropdown.Item
                id="settings"
                icon={Settings01}
                label="Settings"
                onAction={() => navigate({ to: "/settings", state: returnState })}
              />
            </Dropdown.Section>
            {(canUseSensitiveDevTools || showDevToolsLink) && (
              <Dropdown.Section>
                <Dropdown.Separator />
                <Dropdown.SectionHeader className="px-4 pt-1 pb-1 text-xs font-semibold text-brand-600 uppercase tracking-wider">
                  Support
                </Dropdown.SectionHeader>
                {canUseSensitiveDevTools && (
                  <Dropdown.Item
                    id="orgless-accounts"
                    icon={Flag01}
                    label="Orgless Accounts"
                    onAction={() => navigate({ to: "/system/orgless-accounts", state: returnState })}
                  />
                )}
                {showDevToolsLink && (
                  <Dropdown.Item
                    id="dev-tools"
                    icon={Tool02}
                    label="Developer Tools"
                    onAction={() => navigate({ to: "/dev-tools", state: returnState })}
                  />
                )}
              </Dropdown.Section>
            )}
            <Dropdown.Section>
              <Dropdown.Separator />
              <Dropdown.Item
                id="logout"
                icon={LogOut01}
                label="Sign out"
                onAction={logout}
              />
            </Dropdown.Section>
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>

      <OrgSwitcherModal
        isOpen={isOrgSwitcherOpen}
        onClose={() => setIsOrgSwitcherOpen(false)}
      />
    </>
  );
}
